import { useState } from "react";

import { useDispatch } from "react-redux";
import { Table, Button, Form, message, Spin } from "antd";

import Popup from "../../Popup";
import "../../../styles/components/approverForm.scss";

import { PlusCircleOutlined } from "@ant-design/icons";

import { showPopup, hidePopup } from "../../../state/slices/generalSlice";
import {
  applySortableColumns,
  extractPositionDates,
  getLocalDateString,
  isAssignmentInactiveByDate,
  normalizeEmployeeStatus,
} from "../../../utils/generalUtils";

import AdminTableForm from "./AdminTableForm";

function AdminTable({
  tableColumn,
  tableData,
  responseData,
  popupSlice,
  popupName,
  docuType,
  formFields,
  onRefresh,
  onAdd,
  onEdit,
  onAddBulk,
  submitLoading,
  isEmployee,
  positionData,
  hideAdd,
}) {
  const [form] = Form.useForm();
  const [action, setAction] = useState("add");
  const [editingRecord, setEditingRecord] = useState(null);
  const [deletedPositionAssignmentIds, setDeletedPositionAssignmentIds] =
    useState([]);

  const dispatch = useDispatch();

  const buildRequestData = (values) => {
    const payload = { ...values };

    if (isEmployee) {
      const normalizedPositions = Array.isArray(values?.positions)
        ? values.positions
            .map((position) =>
              typeof position === "object"
                ? {
                    position_id: position?.position_id,
                    position_start_date: position?.position_start_date || null,
                    position_end_date: position?.position_end_date || null,
                    position_assignment_id:
                      position?.position_assignment_id || null,
                  }
                : {
                    position_id: position,
                    position_start_date: null,
                    position_end_date: null,
                    position_assignment_id: null,
                  }
            )
            .filter(
              (position) =>
                Number.isInteger(Number(position.position_id)) &&
                Number(position.position_id) > 0
            )
            .map((position) => ({
              position_id: Number(position.position_id),
              position_start_date: position.position_start_date,
              position_end_date: position.position_end_date,
              position_assignment_id: position.position_assignment_id
                ? Number(position.position_assignment_id)
                : null,
            }))
        : [];

      payload.positions = normalizedPositions;
      payload.deleted_position_assignment_ids = deletedPositionAssignmentIds;

      if (typeof values?.is_active === "boolean") {
        payload.is_active = values.is_active;
      }

      delete payload.position_ids;
    }

    return payload;
  };

  const markAssignmentForDeletion = (assignmentId) => {
    const normalizedId = Number(assignmentId);
    if (!Number.isInteger(normalizedId) || normalizedId <= 0) {
      return;
    }

    setDeletedPositionAssignmentIds((prev) =>
      prev.includes(normalizedId) ? prev : [...prev, normalizedId]
    );
  };

  const hasDuplicateActivePosition = (positions = []) => {
    const today = getLocalDateString();
    const seen = new Set();

    for (const position of positions) {
      const isInactive = isAssignmentInactiveByDate(
        position?.position_end_date,
        today
      );
      if (isInactive) continue;

      const key = String(position.position_id);
      if (seen.has(key)) {
        return true;
      }
      seen.add(key);
    }

    return false;
  };

  const handleSubmit = async (values) => {
    const isAdd = action === "add";
    const handler = isAdd ? onAdd : onEdit;

    if (!handler) {
      console.error(
        `Missing ${isAdd ? "onAdd" : "onEdit"} handler for AdminTable`
      );
      return;
    }

    const requestData = buildRequestData(values);

    if (isEmployee && hasDuplicateActivePosition(requestData?.positions)) {
      message.error("Duplicate active position assignment found in form.");
      return;
    }

    try {
      const result = await handler(requestData);

      if (result) {
        console.log("Successful:", result);
        setDeletedPositionAssignmentIds([]);
        dispatch(hidePopup(popupName));
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      console.error("Failed", err);
    }
  };

  const handleRowClick = (record) => {
    if (docuType === "submissions") return;

    setAction("edit");
    setEditingRecord(record);
    setDeletedPositionAssignmentIds([]);

    if (isEmployee === true) {
      const normalizedEmployee = normalizeEmployeeStatus(record);
      const formattedPositions = Array.isArray(record.positions)
        ? record.positions.map((position) => {
        const { position_start_date, position_end_date } =
          extractPositionDates(position);

        return {
          position_assignment_id: Number(
            position?.position_assignment_id ?? position?.pivot?.id
          ),
          position_id: Number(position?.position_id),
          position_start_date: position_start_date || null,
          position_end_date: position_end_date || null,
        };
      })
        : [];

      form.setFieldsValue({
        ...record,
        is_active: normalizedEmployee.is_active,
        employment_end_date: normalizedEmployee.employment_end_date,
        positions: formattedPositions,
      });
    } else {
      form.setFieldsValue(record);
    }

    dispatch(showPopup(popupName));
  };

  // global function that searches whether there is a sortable object
  const colmunnsWithSorting = applySortableColumns(tableColumn);

  return (
    <>
      <div className="admin">
        {/* <UploadExcel
          uploadUrl={addBulkUrl}
          onSuccess={onRefresh}
          buttonText="Upload File"
          accept=".xls,.xlsx"
        /> */}
        {docuType === "submissions" || hideAdd ? null : (
          <div style={{ display: "flex", flexDirection: "row-reverse" }}>
            <Button
              type="primary"
              size="large"
              icon={<PlusCircleOutlined />}
              style={{ background: "#862633", marginBottom: "10px" }}
              onClick={() => {
                form.resetFields();
                if (isEmployee) {
                  form.setFieldsValue({
                    is_active: true,
                    employment_end_date: null,
                  });
                }
                setAction("add");
                setEditingRecord(null);
                setDeletedPositionAssignmentIds([]);
                dispatch(showPopup(popupName));
              }}
            >
              Add
            </Button>
          </div>
        )}

        <Table
          columns={colmunnsWithSorting}
          dataSource={tableData}
          size="small"
          rowKey={(record) => record.id || record.key}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
          })}
          footer={() => (
            <div style={{ textAlign: "center", color: "#555" }}>
              Showing {tableData?.length || 0}{" "}
              {tableData?.length === 1 ? "result" : "results"}
            </div>
          )}
        />
        <Popup
          isOpen={popupSlice}
          title={docuType.charAt(0).toUpperCase() + docuType.slice(1) + " Form"}
          onCancel={() => {
            form.resetFields();
            setDeletedPositionAssignmentIds([]);
            dispatch(hidePopup(popupName));
          }}
          footer={null}
          width={1000}
        >
          <AdminTableForm
            form={form}
            formFields={formFields}
            action={action}
            loading={submitLoading}
            onSubmit={handleSubmit}
            onMarkDeleteAssignment={markAssignmentForDeletion}
            positionData={positionData}
            tableData={tableData}
            isEmployee={isEmployee}
            employeePhoto={editingRecord?.profile_photo}
          />
        </Popup>
      </div>
    </>
  );
}

export default AdminTable;
